// New notifications pop up top-right on every page (0278) — the same stack,
// the same rules, as the live map's alerts: urgent on top, three at most, and
// a heads-up held back while three urgent ones are up (planAlertStack).
//
// What does NOT toast:
//   · anything already there when the app opened — that is not news, and the
//     bell lists it (seeded silently, like useLiveMapAlerts);
//   · `info` rows — news with nothing to do, bell only;
//   · an SOS — it has its own surfaces (the banner, the map's lane), which
//     never auto-dismiss, so a 20-second toast would only be noise beside them;
//   · on /live-map, the three urgent booking events — the map raises those
//     itself from live data, and the same incident must not pop up twice.
//
// On /live-map the toasts join the map's own stack, which is aligned under the
// SOS lane; everywhere else they use the attention toaster in AppLayout.

import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import {
  HEADS_UP_ALERT_MS,
  isUrgent,
  noteUrgentGone,
  noteUrgentShown,
  planAlertStack,
  URGENT_ALERT_MS,
  urgentShowing,
} from "../live-map/live-map.alerts";
import { LIVE_MAP_TOASTER_ID } from "../live-map/useLiveMapAlerts";
import {
  type AdminNotification,
  useNotifications,
  useOpenNotification,
} from "./notifications.hooks";

export const ATTENTION_TOASTER_ID = "attention";

const MAP_RAISES_ITSELF = new Set(["payment_issue", "no_show_reported", "booking_running_late"]);

export function useNotificationToasts() {
  const { data } = useNotifications();
  const location = useLocation();
  const open = useOpenNotification();
  const openRef = useRef(open);
  openRef.current = open;
  const onMapRef = useRef(false);
  onMapRef.current = location.pathname === "/live-map";

  const seen = useRef<Set<string> | null>(null);

  useEffect(() => {
    if (!data) return;
    if (seen.current === null) {
      seen.current = new Set(data.map((row) => row.id));
      return;
    }
    const fresh = data.filter((row) => !seen.current?.has(row.id) && !row.readAt);
    for (const row of fresh) seen.current.add(row.id);

    const onMap = onMapRef.current;
    const candidates = fresh
      .filter((row) => row.level !== "info" && row.event !== "therapist_sos")
      .filter((row) => !(onMap && MAP_RAISES_ITSELF.has(row.event)))
      .map((row) => ({
        row,
        tone: row.level === "urgent" ? ("danger" as const) : ("warning" as const),
      }));

    // The toaster these land in decides which count applies: on the map they
    // share the stack with the map's own alerts, elsewhere they have their own.
    const toasterId = onMap ? LIVE_MAP_TOASTER_ID : ATTENTION_TOASTER_ID;

    for (const item of planAlertStack(candidates, urgentShowing(toasterId))) {
      const urgent = isUrgent(item);
      const toastId = `notification-${item.row.id}`;
      const settle = () => noteUrgentGone(toasterId, toastId);
      if (urgent) noteUrgentShown(toasterId, toastId);
      const row: AdminNotification = item.row;
      (urgent ? toast.error : toast.warning)(row.title, {
        id: toastId,
        toasterId,
        description: row.body,
        duration: urgent ? URGENT_ALERT_MS : HEADS_UP_ALERT_MS,
        action: { label: "Open", onClick: () => openRef.current(row) },
        onDismiss: settle,
        onAutoClose: settle,
      });
    }
  }, [data]);
}
